<template>
  <ui-card name="user.segments" rounded :error="error">
    <section class="overview">
      <div class="user-media media">
        <div class="media-left">
          <i class="material-icons icon is-large">{{ getIcon(activeSegment.id) }}</i>
        </div>
        <div class="media-content">
          <h2 class="segment-display-name">{{ activeSegment.display_name }}</h2>
          <p class="segment-description">{{ activeSegment.description }}</p>
        </div>
      </div>
    </section>

    <section class="controls">
      <h3>{{ $t('user.segments.categories') }}</h3>
      <div class="categories">
        <button
          v-for="(category, index) in categories"
          :key="index"
          class="button is-small"
          :class="{'is-active': activeCategoryId === category.id}"
          @click="setCategory(category.id)"
        >{{ $t(category.keypath) }}</button>
      </div>

      <h3>{{ $t('user.segments.segments') }}</h3>
      <div class="segments">
        <div v-if="segmentsByActiveCategory.length">
          <a
            v-for="(segment, index) in segmentsByActiveCategory"
            :key="index"
            class="button is-small"
            :class="{'is-active': activeSegmentId === segment.id}"
            @click="activeSegmentId = segment.id"
          >
            <i class="material-icons">{{ getIcon(segment.id) }}</i>
            <span class="text">{{ segment.display_name }}</span>
          </a>
        </div>
        <p v-else>{{ $t('user.segments.no_categories') }}</p>
      </div>
    </section>
  </ui-card>
</template>

<script>
import { segmentCategoryTypes } from "config/settings";
import { groupBy } from "lodash";
import icons from "config/icons";
import { mapGetters, mapState } from "vuex";

export default {
  data() {
    return {
      activeCategoryId: null,
      activeSegmentId: null
    };
  },

  computed: {
    ...mapGetters("account", ["segmentDefinitions"]),
    segments() {
      return this.$store.state.user.segments.map(segment => ({
        ...segment,
        ...this.segmentDefinitions[segment.segment_definition_id]
      }));
    },
    definitionsByCategory() {
      return groupBy(this.segmentDefinitions, "category");
    },
    categories() {
      return segmentCategoryTypes.filter(
        category => this.definitionsByCategory[category.id]
      );
    },
    segmentsByActiveCategory() {
      return this.segments.filter(
        segment => segment.category === this.activeCategoryId
      );
    },
    activeSegment() {
      return (
        this.segments.find(segment => segment.id === this.activeSegmentId) || {}
      );
    },
    error() {
      return this.segments.length === 0 ? "no_segments" : null;
    }
  },

  mounted() {
    this.setCategory(this.categories[0].id);
  },

  activated() {
    this.setCategory(this.categories[0].id);
  },

  methods: {
    setCategory(id) {
      this.activeCategoryId = id;

      if (
        this.segmentsByActiveCategory &&
        this.segmentsByActiveCategory.length > 0
      ) {
        this.activeSegmentId = this.segmentsByActiveCategory[0].id;
      }
    },

    getIcon(id) {
      const category = String(id).match(/\w+\.\w+/);

      return (
        icons.segments.ids[id] ||
        icons.segments.categories[category] ||
        "bookmark"
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~scss/utilities/initial-variables.scss";
@import "~scss/utilities/derived-variables.scss";
@import "~scss/utilities/mixins.scss";

section {
  padding: 1rem;
}

.overview {
  background-color: $light;
  border-bottom: 1px solid rgba($black, 0.06);

  .icon {
    width: 56px;
    height: 56px;
    border: 1px solid rgba($black, 0.06);
    font-size: 20px;
    background: $white;
    color: rgba($black, 0.87);
  }

  hr {
    margin-top: 1rem;
    margin-bottom: 1rem;
    background-color: rgba($black, 0.08);
  }
}

.segment-display-name {
  font-size: 0.875rem;
  font-weight: $weight-medium;
  line-height: 1.14;
  color: $black;
}

.segment-description {
  font-size: 0.875rem;
  line-height: 1.14;
  color: rgba($black, 0.87);
  margin-top: 0.5rem;
}

.controls {
  h3 {
    font-size: 0.875rem;
    color: rgba($black, 0.87);
    line-height: 1.14;

    &:not(:first-child) {
      margin-top: 1.5rem;
    }
  }
}

.button {
  padding-top: 0.125rem;
  padding-bottom: 0.125rem;
  background-color: rgba($black, 0.06);
  color: rgba($black, 0.87);
  font-weight: $weight-medium;
  border-radius: 2px;
  margin-bottom: 0.25rem;
  transition: $transition;
  transition-property: color, background-color;

  &:active,
  &.is-active {
    background-color: $blue;
    color: $white;
  }

  &:not(:last-child) {
    margin-right: 0.25rem;
  }
}

.categories,
.segments {
  margin-top: 1rem;
}

.segments {
  .button {
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    justify-content: flex-start;
    width: calc(50% - 4px);
    margin-bottom: 0.25rem;

    .text {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    i {
      font-size: 0.875rem;
      margin-right: 0.375rem;
    }
  }
}
</style>
